import React from 'react';
import { cn } from '@/lib/utils';
import { Copy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { IconButton } from '@/components/ui/icon-button';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { useTranslation } from '@/hooks/useTranslation';
import { useProcessManager } from '../hooks/useProcessManager';
import { ProcessForm } from './ProcessForm';
import { ArchiveAction } from './ArchiveAction';

import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';

type ProcessManagerPanelProps = {
  companyName: string;
  traceabilityApiClient: TraceabilityApiClient | null;
};

export const ProcessManagerPanel: React.FC<ProcessManagerPanelProps> = ({ companyName, traceabilityApiClient }) => {
  const { t } = useTranslation();
  const {
    formProcess,
    setFormProcess,
    selectedId,
    formError,
    isLoading,
    isSaving,
    isArchiving,
    archiveConfirm,
    setArchiveConfirm,
    showArchived,
    setShowArchived,
    searchQuery,
    setSearchQuery,
    typeFilter,
    setTypeFilter,
    selectedProcess,
    isEditing,
    activeProcesses,
    groupedProcesses,
    toolReferences,
    materials,
    materialLots,
    machines,
    activeArticles,
    handleAddOrUpdate,
    handleArchive,
    handleRestore,
    handleSelectProcess,
    handleNewProcess,
    handleDuplicate,
    handleDuplicateFrom,
  } = useProcessManager(companyName, traceabilityApiClient);

  return (
    <div className="flex flex-col gap-4">
      {formError && <Callout tone="error">{formError}</Callout>}

      {/* Mobile process selector */}
      <div className="mb-4 md:hidden">
        <SimpleSelect
          aria-label={t('traceability.processManager.list.title')}
          value={selectedId !== undefined ? String(selectedId) : null}
          onChange={(next) => {
            const id = next ? Number(next) : undefined;
            const p = id !== undefined ? activeProcesses.find((p) => p.id === id) : undefined;
            if (p) handleSelectProcess(p);
            else handleNewProcess();
          }}
          disabled={isLoading || isSaving || isArchiving}
          noneLabel={t('traceability.processManager.form.new')}
          options={activeProcesses.map((p) => ({
            value: p.id !== undefined ? String(p.id) : p.name,
            label: `[${p.toolReference}] — ${p.name}`,
          }))}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        {/* Form panel */}
        <ProcessForm
          formProcess={formProcess}
          setFormProcess={setFormProcess}
          toolReferences={toolReferences}
          materials={materials}
          materialLots={materialLots}
          activeArticles={activeArticles}
          machines={machines}
          isEditing={isEditing}
          isSaving={isSaving}
          isLoading={isLoading}
          onSave={handleAddOrUpdate}
          onNew={handleNewProcess}
          onDuplicate={handleDuplicate}
        />

        {/* List panel — desktop only */}
        <Card padding="sm" className="hidden md:flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-foreground">
              {t('traceability.processManager.list.title')}
            </p>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-border accent-primary"
                />
                {t('traceability.processManager.list.showArchived')}
              </label>
            </div>
          </div>

          {/* Search + type filter */}
          <div className="flex gap-2">
            <input
              type="text"
              className="flex-1 rounded-xl border border-border bg-card px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder={t('traceability.processManager.list.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SimpleSelect
              className="w-auto"
              value={typeFilter}
              onChange={(next) => setTypeFilter((next ?? 'all') as 'all' | 'standard' | 'test')}
              aria-label={t('traceability.processManager.list.filterAll')}
              options={[
                { value: 'all', label: t('traceability.processManager.list.filterAll') },
                { value: 'standard', label: t('traceability.processManager.list.filterStandard') },
                { value: 'test', label: t('traceability.processManager.list.filterTest') },
              ]}
            />
          </div>

          <div className="max-h-72 space-y-2 overflow-auto pr-1">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" className="text-primary" />
              </div>
            )}
            {!isLoading && groupedProcesses.size === 0 && (
              <EmptyState size="compact" title={t('traceability.processManager.list.empty')} />
            )}
            {!isLoading &&
              Array.from(groupedProcesses.entries()).map(([toolRef, procs]) => (
                <div key={toolRef}>
                  <div className="sticky top-0 flex items-center gap-2 bg-card/80 px-1 py-1 backdrop-blur-sm">
                    <span className="font-mono text-xs font-semibold text-foreground">
                      {toolRef}
                    </span>
                    <Badge size="sm">{procs.length}</Badge>
                  </div>
                  {procs.map((proc) => (
                    <div
                      key={proc.id ?? proc.name}
                      role="button"
                      tabIndex={isSaving || isArchiving ? -1 : 0}
                      aria-disabled={isSaving || isArchiving}
                      onClick={() => { if (!isSaving && !isArchiving) handleSelectProcess(proc); }}
                      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isSaving && !isArchiving) handleSelectProcess(proc); }}
                      className={cn(
                        'group w-full cursor-pointer rounded-xl border px-4 py-3 text-left shadow-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
                        proc.archived
                          ? 'opacity-50 border-border bg-card'
                          : cn(
                              'transition hover:-translate-y-0.5 hover:shadow-md',
                              proc.id === selectedId
                                ? 'border-primary/60 bg-primary/10 text-primary'
                                : 'border-border bg-card hover:border-primary/40 hover:bg-primary/5'
                            )
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex min-w-0 flex-col">
                            <p className="truncate text-sm font-semibold">{proc.name}</p>
                            {proc.material?.name && (
                              <p className="truncate text-xs text-muted-foreground">
                                {proc.material.name}
                              </p>
                            )}
                          </div>
                          <Badge
                            tone={proc.processType === 'standard' ? 'success' : 'warning'}
                            size="sm"
                            caps
                          >
                            {t(`traceability.processSelection.type.${proc.processType}`)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          {proc.archived && (
                            <Badge size="sm" caps>
                              {t('traceability.processManager.list.archivedBadge')}
                            </Badge>
                          )}
                          {!proc.archived && (
                            <IconButton
                              icon={Copy}
                              label={t('traceability.processManager.form.submit.duplicate')}
                              className="-my-2 hidden group-hover:inline-flex"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateFrom(proc);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
          </div>

          <ArchiveAction
            selected={selectedProcess}
            i18nPrefix="traceability.processManager"
            archiveConfirm={archiveConfirm}
            isArchiving={isArchiving}
            isSaving={isSaving}
            onArchiveClick={() => setArchiveConfirm(true)}
            onArchiveConfirm={handleArchive}
            onArchiveCancel={() => setArchiveConfirm(false)}
            onRestore={handleRestore}
          />
        </Card>
      </div>
    </div>
  );
};
